export enum CONNECTIVITY_TYPE {
  WIFI = 'WIFI',
  SIM_CARD = 'SIM_CARD'
}

export enum RESELLER_PAYMENT_STATUS {
  TO_BE_PAID = "TO_BE_PAID",
  PAID = "PAID"
}

export interface ConnectivityAddress {
  building: string;
  city: string;
  id: number;
  number: string;
  province: string;
  zip: string;
  scale?: string;
  company_name?: string;
}

export interface ConnectivityItem {
  provider_preview: {
    id: number;
    name: string;
  };
  id: number;
  company_id: number;
  type: CONNECTIVITY_TYPE;
  address: ConnectivityAddress;
  is_locked: boolean;
}

export interface UIConnectivityItem extends ConnectivityItem {
  loading?: boolean;
}

export interface ConnectionDetails {
  id: number;
  type: CONNECTIVITY_TYPE;
  provider_name: string;
  company_id: number;
  contacts: ConnectivityContact[],
  connection_status: number;
  connection_status_description: string;
  reseller_payment?: {
    id: number;
    reseller_id: string;
    reseller_payment_status: RESELLER_PAYMENT_STATUS;
  };
  provider_reference_id: number;
  address: ConnectivityAddress,
  technical_parameters: TechnicalParams;
  is_locked?: boolean;
}

export interface TechnicalParams {
  id: number;
  ip_address_v6?: string;
  ip_address_v4: string;
  user_name: string;
  password: string;
  user_vlan: string;
  smtp?: string;
  primary_dns?: string;
  secondary_dns?: string;
}

export interface ConnectivityContact {
  id: number;
  first_name: string;
  last_name: string;
  email: string
  phone_numbers: string[]
}

export interface ConnectionStatus {
  account_twt: number;
  ip_address: string;
  VoIP: boolean;
  successful_pings: number;
  total_pings: number;
  date: Date;
}

export interface ConnectionPing {
  ping_status: boolean;
  ping_description: string;
}

export interface ConnectionEquipment {
  id: number;
  connection_id: number;
  equipment_catalog_entry_id: number;
  model: string;
  company_name?: string;
  mac_address?: string;
}

export interface ConnectivityEquipmentBase {
  type: ConnectivityCatalogType;
  company_id: number;
  connection_id: number;
  wifi_equipment_catalog_entry_id?: number;
}
export interface ConnectionInternetEquipment extends ConnectivityEquipmentBase {
  type: ConnectivityCatalogType.internet_key;
  imei: string;
  serial_number: string;
  number: string;
  iccid: string;
  puk: string;
  pin: string;
  status: string;
  activation_date: string;
  company_name: never;
  mac_address: never;
}
export interface ConnectionModemEquipment extends ConnectivityEquipmentBase {
  type: ConnectivityCatalogType.modem_router;
  company_name: string;
  mac_address: string;
  imei: never;
  serial_number: never;
  number: never;
  iccid: never;
  puk: never;
  pin: never;
  status: never;
  activation_date: never;
}

export type ConnectionEquipmentInfo = ConnectionInternetEquipment | ConnectionModemEquipment;

export interface ConnectionEquipmentDetails {
  id: number;
  equipment_catalog_entry?: ConnectivityCatalogEntry;
  wifi: ConnectionDetailsExtended;
  equipment_type: ConnectivityCatalogType;
  imei?: string;
  serial_number?: string;
  number?: string;
  iccid?: string;
  puk?: string;
  pin?: string;
  status?: string;
  activation_date?: string;
  company_name?: string;
  mac_address?: string;
}

export interface ConnectivityCatalogEntry {
  id: number;
  model: string;
  brand: string;
  type: ConnectivityCatalogType;
  links: ConnectivityCatalogLink[];
  created_at: Date;
  updated_at: Date;
}

export interface ConnectivityCatalogLink {
  id: number;
  name: string;
  contentType: ContentType;
  createdAt: Date;
  description: string;
  url: string;
}

export enum ContentType {
  csv = 'CSV',
  doc = 'DOC',
  docx = 'DOCX',
  jpeg = 'JPEG',
  pdf = 'PDF',
  pjpeg = 'PJPEG',
  png = 'PNG',
  tiff = 'TIFF',
  txt = 'TXT',
  xls = 'XLS',
  xlsx = 'XLSX'
}

export interface ConnectionDetailsExtended extends ConnectionDetails {
  ping?: PING_STATUS;
  product: string;
  has_4g_backup: boolean;
  partner_id?: number;
  technology: string;
}

export enum PING_STATUS {
  OK = 'OK',
  TIMEOUT = 'TIMEOUT',
  No_ROUTE_TO_HOST = 'No_ROUTE_TO_HOST'
}

export enum ConnectivityCatalogType {
  modem_router = 'MODEM_ROUTER',
  internet_key = 'INTERNET_KEY'
}

export const CatalogType: string[] = [
  'MODEM_ROUTER',
  'INTERNET_KEY'
]

export const ConnectivityEquipmentStatus: string[] = [
  'ACTIVE',
  'KO'
]

export enum ConnectivityWiFiType {
  wifi = 'WIFI',
  sim_card = 'SIM_CARD'
}

export interface AddConnectionRequest {
  company_id: number;
  provider_reference_id: number;
}

export const MAX_FILE_SIZE = 5242880; // 5MB
export const ACCEPTED_FILE_FORMATS = ['csv', 'doc', 'docx', 'jpeg', 'pdf', 'pjpeg', 'png', 'tiff', 'txt', 'xls', 'xlsx'];

export interface ConnectivityCatalogEntryGetRequest extends ConnectivityFilters {
  page?: number;
  size?: number;
}

export interface ConnectivityCatalogEntry {
  id: number;
  model: string;
  brand: string;
  type: ConnectivityCatalogType;
  links: ConnectivityCatalogLink[];
  created_at: Date;
  updated_at: Date;
}

export interface ConnectivityCatalogLink {
  id: number;
  name: string;
  contentType: ContentType;
  createdAt: Date;
  description: string;
  url: string;
}

export interface CatalogItem {
  brand: string;
  model: string;
  type: ConnectivityCatalogType;
}

export interface CatalogItemChanged {
  value: CatalogItem;
  isValid: boolean;
}

export interface ConnectivityFilters {
  model?: string;
  brand?: string;
  type?: ConnectivityCatalogType;
  created_at_from?: string;
  created_at_to?: string;
  updated_at_from?: string;
  updated_at_to?: string;
}

export interface ConnectivityAddDocumentRequest {
  document: File;
  request: ConnectivityLinkAddRequest;
}

export interface ConnectivityLinkAddRequest {
  name: string;
  content_type: ContentType;
  equipment_catalog_entry_id: number;
  description: string;
}